// File: src/features/admin/OrderlistApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// ---------- Existing Order Types ----------
export interface OrderItem {
  id: number;
  product_id: number;
  product_name: string | null;
  product_quantity: number;
  unit_price: string;
  discount_type: number;
  discount: string;
  itemTotal: string;
  discountAmt: string;
  taxAmount: string;
  shippingTotal: string;
  // Per-product cancellation (set when this specific item was cancelled
  // out of a multi-product order, via /cancelOrder with items[])
  cancelled_qty?: number;
  cancellable_qty?: number;
  is_cancelled?: boolean;
  is_partially_cancelled?: boolean;
  cancel_reason?: string | null;
  cancelled_at?: string | null;
}

export interface OrderComment {
  id?: number;
  order_id: number;
  user_type: 'admin' | 'user';
  user_id: number;
  user_name?: string;
  comment: string;
  created_at?: string;
  status?: number;
}

export interface Order {
  delivery_agent_name: any;
  id: number;
  user_id: number | string | null | undefined;
  user_name: string;
  order_date: string;
  total_amount: string;
  total_order_amount: string;
  total_shipping_charge: string;
  total_discount_amount: string;
  total_tax_amount: string;
  status: number;
  status_text: string;
  delivery_address_id: number;
  delivery_model_id: number | null;
  items: OrderItem[];
  payment_status?: string;
  comments?: OrderComment[];
  comment?: string;
  pickup_code?: string;
  qr_code?: string;
  order_type?: 'regular' | 'guest' | string;
  orderType?: string;
  type?: string;
  guest_order?: number | boolean | string;
  is_guest?: number | boolean | string;
  cash_on_delivery?: number;
  pay_later?: number;
  reward_discount?: number;
  coupon_discount?: number;
  gift_delivery?: number;
  gift_message?: string;
  open_box_charge?: string | number;
  order_items_count?: number;
  total_warranty_amount? : number;
}

export interface OrdersResponse {
  error: boolean;
  message: string;
  orders: Order[];
}

export interface OrderStatusUpdateRequest {
  order_id: number;
  status: number;
  comment?: string;
}

export interface DeleteOrderRequest {
  order_id: number;
}

export interface OrderCommentsRequest {
  order_id: number;
}

export interface OrderCommentsResponse {
  error: boolean;
  message: string;
  comments: OrderComment[];
}

export interface DeleteCommentRequest {
  comment_id: number;
  order_id: number;
}

export interface CancelOrderRequest {
  order_id: number;
  order_type?: 'regular' | 'guest';
  cancel_reason?: string;
}

export interface CancelOrderResponse {
  success: boolean;
  message: string;
  status_code: number;
}

export interface GenerateQRRequest {
  order_id: number;
  order_type: 'regular' | 'guest';
}

export interface GenerateQRResponse {
  success: boolean;
  message: string;
  pickup_code: string;
  qr_code: string;
  order_id?: string;
  order_type?: string;
}

export interface GenerateInvoiceRequest {
  order_id: number;
  user_id?: string | number | null;
}

export interface GenerateInvoiceResponse {
  error: boolean;
  message: string;
  invoice_no: string;
  download_url: string;
}

export interface GenerateCombinedRequest {
  order_id: number;
  user_id?: string | number | null;
  order_type?: 'regular' | 'guest';
}

export interface GenerateCombinedResponse {
  error: boolean;
  message: string;
  invoice?: {
    invoice_no: string;
    download_url: string;
  };
  qr?: {
    success: boolean;
    pickup_code: string;
    qr_code: string;
    qr_image_url?: string;
  };
}

// ---------- Return Items Types (NEW) ----------
export interface ReturnItem {
  id: number;
  return_id: number;
  order_id: number;
  order_type: 'regular' | 'guest';
  user_id: number | null;
  guest_user_id: number | null;
  product_id: number;
  order_item_id: number;
  return_qty: number;
  refund_mode: 'Wallet' | 'bank_transfer' | 'cash';
  request_type: string;           // 'Refund' or 'replacement' (case may vary)
  status: number;
  price: string;
  total: string;
  refund_amount: number;
  reason: string;
  notes: string | null;
  created_at: string;
  product_name?: string;
  // Who actually has to service this return: a vendor product is fulfilled
  // (and now approved/progressed) by that vendor, a Voto in-house product by
  // admin. Surfaced by /user/return-itemslist so the two can be told apart
  // in the list instead of every row looking like a Voto return.
  vendor_id?: number | null;
  is_vendor_product?: boolean;
  vendor_name?: string | null;
  user_email?: string;
  transaction_id?: string;
  refund_destination?: string;
  // order summary fields (optional)
  total_amount?: string;
  guest_total_amount?: string;
  cash_on_delivery?: number;
  guest_cod?: number;
  pay_later?: number;
}

export interface ReturnItemsResponse {
  success: boolean;
  message: string;
  total_records: number;
  data: ReturnItem[];
}

export interface ReturnApproveRequest {
  return_id: number;
  type?: number;  // new status (legacy status-update flow)
  action?: string;  // e.g. 'approve' (resolution-approval flow)
  resolution?: string;  // e.g. 'replacement' | 'wallet' | 'refund' (resolution-approval flow)
}

export interface ReturnApproveResponse {
  success: boolean;
  status: number;
  status_text: string;
  message?: string;
}

// ---------- Constants (shared) ----------
export const ORDER_STATUS_OPTIONS = [
  { value: 0, label: 'Payment Pending', color: '#ff9800' },
  { value: 1, label: 'Payment Success', color: '#2196f3' },
  { value: 2, label: 'Processing', color: '#4caf50' },
  { value: 3, label: 'Pickup Pending', color: '#795548' },
  { value: 4, label: 'Ware House Pickup Pending', color: '#f44336' },
  { value: 5, label: 'In Transit', color: '#511111ff' },
  { value: 6, label: 'Out for delivery', color: '#427489ff' },
  { value: 7, label: 'Delivered', color: '#7ca14dff' },
  { value: 8, label: 'Not delivered', color: '#c474a0ff' },
  { value: 9, label: 'Cancelled by user', color: '#f4559aff' },
  { value: 10, label: 'Return and replacement', color: '#c6b0ecff' },
  { value: 11, label: 'Return and refund', color: '#2b061aff' },
  { value: 12, label: 'Order cancelled by admin', color: '#96e8deff' },
  { value: 13, label: 'Order Delayed', color: '#ff002bff' },
  { value: 14, label: 'Return refund in progress', color: '#9c27b0' },
  { value: 15, label: 'Verified and item picked', color: '#3f51b5' },
  { value: 16, label: 'Partially delivered', color: '#ff9800' },
  { value: 17, label: 'Return', color: '#607d8b' },
  { value: 18, label: 'Return Replacement item picked', color: '#FF6B35' },
  { value: 19, label: 'Return item verified and replacement delivered', color: '#6A0572' },
  { value: 20, label: 'Payment failed', color: '#edc573ff' },
  { value: 21, label: 'Refund Completed', color: '#1A936F' },
  { value: 22, label: 'Replacement Completed', color: '#114B5F' },
  { value: 23, label: 'Replacement Ready for Pickup', color: '#FFA500' },
  { value: 24, label: 'Refund initiated', color: '#dad0be' },
  { value: 25, label: 'Replacement in progress', color: '#dad0be' },
];

export const getStatusText = (status: number): string => {
  const option = ORDER_STATUS_OPTIONS.find(opt => opt.value === status);
  return option ? option.label : `Status ${status}`;
};

export const getStatusColor = (status: number): string => {
  const option = ORDER_STATUS_OPTIONS.find(opt => opt.value === status);
  return option ? option.color : '#9e9e9e';
};

// ---------- Order Helpers (existing) ----------
const parseCommentsString = (commentString: string): OrderComment[] => {
  if (!commentString || typeof commentString !== 'string') return [];

  const commentParts = commentString.split(/(?=\d+\.\s*\[)/);

  return commentParts
    .filter((part) => part.trim())
    .map((part, index) => {
      const dateMatch = part.match(/\[([^\]]+)\]/);
      const userTypeMatch = part.match(/\(([^)]+)\)/);
      const commentMatch = part.match(/- (.+?)(?=\s*\d+\.\s*\[|$)/s);

      const userType = userTypeMatch?.[1] === 'admin' ? 'admin' : 'user';
      const dateString = dateMatch?.[1];
      let parsedDate: string;

      try {
        parsedDate = dateString ? new Date(dateString).toISOString() : new Date().toISOString();
      } catch {
        parsedDate = new Date().toISOString();
      }

      const contentHash = btoa(commentMatch?.[1]?.trim() || part).slice(0, 8);
      const uniqueId =
        Date.now() +
        index +
        Math.random() * 1000 +
        parseInt(contentHash.replace(/[^0-9]/g, '') || '0', 10);

      return {
        id: Math.floor(uniqueId),
        order_id: 0,
        user_type: userType as 'admin' | 'user',
        user_id: userType === 'admin' ? 1 : 0,
        user_name: userType === 'admin' ? 'Admin' : 'User',
        comment:
          commentMatch?.[1]?.trim() ||
          part.replace(/^\d+\.\s*\[.*?\]\s*\([^)]+\)\s*-\s*/, '').trim(),
        created_at: parsedDate,
        status: undefined,
      };
    });
};

const extractStatusFromComment = (commentText: string): number | undefined => {
  const statusKeywords: Record<string, number> = {
    'payment pending': 0,
    'payment success': 1,
    processing: 2,
    'pickup pending': 3,
    'warehouse pickup': 4,
    'in transit': 5,
    'out for delivery': 6,
    delivered: 7,
    'not delivered': 8,
    'cancelled by user': 9,
    'return and replacement': 10,
    'return and refund': 11,
    'order cancelled by admin': 12,
    'order delayed': 13,
    'return in progress': 14,
    'return completed': 15,
    'partially delivered': 16,
    return: 17,
    'return replacement in progress': 18,
    'replacement shipped': 19,
    'payment failed': 20,
    'replacement delivered': 21,
    'replacement completed': 22,
    'replacement in progress': 25
  };

  const lowerComment = commentText.toLowerCase();

  for (const [keyword, status] of Object.entries(statusKeywords)) {
    if (lowerComment.includes(keyword)) return status;
  }

  return undefined;
};

export const determineOrderType = (order: any): 'regular' | 'guest' => {
  const normalize = (v: any) => {
    if (v === null || v === undefined) return '';
    const s = String(v).trim().toLowerCase();
    return s;
  };

  const possibleTypeFields = [
    order?.order_type,
    order?.orderType,
    order?.type,
    order?.order_type_text,
    order?.orderTypeText,
  ];
  for (const field of possibleTypeFields) {
    const n = normalize(field);
    if (!n) continue;
    if (n.includes('guest')) return 'guest';
    if (n.includes('regular')) return 'regular';
  }

  const guestFlags = [order?.guest_order, order?.is_guest];
  for (const f of guestFlags) {
    const n = normalize(f);
    if (!n) continue;
    if (n === '1' || n === 'true' || n === 'yes') return 'guest';
  }

  const userIdRaw = order?.user_id ?? order?.customer_id ?? '';
  const userIdStr = normalize(userIdRaw);
  if (!userIdStr || userIdStr === '0' || userIdStr === 'null' || userIdStr === 'undefined' || userIdStr === 'none') {
    return 'guest';
  }

  const userName = normalize(order?.user_name);
  if (userName) {
    if (
      userName.includes('guest') ||
      userName.includes('visitor') ||
      userName === 'anonymous' ||
      userName === 'guest user'
    ) {
      return 'guest';
    }
  }

  return 'regular';
};

// ---------- Existing Order API ----------
type ListOrdersArg = { warehouse_id?: number | '' } | void;

export const orderApi = createApi({
  reducerPath: 'orderApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers, { type }) => {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('vendor_token');
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      if (type === 'mutation') {
        headers.set('Content-Type', 'application/x-www-form-urlencoded');
      }
      return headers;
    },
  }),
  tagTypes: ['Order', 'OrderComments'],
  endpoints: (build) => ({
    listOrders: build.query<OrdersResponse, ListOrdersArg>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const formData = new URLSearchParams();

        const warehouseId =
          arg && typeof arg === 'object' && 'warehouse_id' in arg ? (arg as any).warehouse_id : undefined;

        if (
          warehouseId !== undefined &&
          warehouseId !== null &&
          warehouseId !== '' &&
          !Number.isNaN(Number(warehouseId))
        ) {
          formData.append('warehouse_id', String(warehouseId));
        }

        const commonReq = {
          method: 'POST' as const,
          body: formData,
        };

        // Regular orders
        const regularRes = await fetchWithBQ({
          url: 'user/adminlistOrders',
          ...commonReq,
        });

        if (regularRes.error) {
          console.error('❌ listOrders regular endpoint failed:', regularRes.error);
          return { error: regularRes.error as any };
        }

        // Guest orders (best-effort; if it fails we still return regular orders)
        const guestRes = await fetchWithBQ({
          url: 'user/adminGuestListOrders',
          ...commonReq,
        });

        if (guestRes.error) {
          console.warn('⚠️ adminGuestListOrders failed, returning only regular orders.', guestRes.error);
        }

        const rawRegular = (regularRes.data || {}) as any;
        const rawGuest = (!guestRes.error && guestRes.data ? guestRes.data : {}) as any;

        const extractOrders = (payload: any): any[] => {
          if (!payload) return [];
          if (Array.isArray(payload.orders)) return payload.orders;
          if (Array.isArray(payload.data)) return payload.data;
          if (Array.isArray(payload.order_list)) return payload.order_list;
          return [];
        };

        const combinedRawOrders = [
          ...extractOrders(rawRegular),
          ...extractOrders(rawGuest),
        ];

        const transformedOrders: Order[] = combinedRawOrders.map((order: any) => {
          let comments: OrderComment[] = [];

          if (Array.isArray(order.comments)) {
            comments = order.comments;
          } else if (typeof order.comment === 'string' && order.comment.trim()) {
            comments = parseCommentsString(order.comment).map((comment) => ({
              ...comment,
              order_id: order.id,
              status: extractStatusFromComment(comment.comment),
            }));
          } else if (typeof order.comments === 'string' && order.comments.trim()) {
            comments = parseCommentsString(order.comments).map((comment) => ({
              ...comment,
              order_id: order.id,
              status: extractStatusFromComment(comment.comment),
            }));
          }

          const orderType = determineOrderType(order);

          return {
            ...order,
            comments,
            order_type: orderType,
          } as Order;
        });

        const message =
          rawRegular?.message ||
          rawGuest?.message ||
          (warehouseId ? 'Orders fetched successfully for selected warehouse' : 'Orders fetched successfully');

        return {
          data: {
            error: rawRegular?.error || false,
            message,
            orders: transformedOrders,
          },
        };
      },
      providesTags: ['Order'],
    }),

    updateOrderStatus: build.mutation<OrdersResponse, OrderStatusUpdateRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('order_id', data.order_id.toString());
        formData.append('status', data.status.toString());
        if (data.comment) formData.append('comment', data.comment);

        return {
          url: 'user/updateOrderStatus',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Order', 'OrderComments'],
    }),

    deleteOrder: build.mutation<any, DeleteOrderRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('order_id', data.order_id.toString());

        return {
          url: 'user/deleteOrder',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Order'],
    }),

    getOrderComments: build.query<OrderCommentsResponse, OrderCommentsRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('order_id', data.order_id.toString());

        return {
          url: 'user/getOrderComments',
          method: 'POST',
          body: formData,
        };
      },
      providesTags: ['OrderComments'],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || '',
        comments: response.comments || [],
      }),
    }),

    deleteComment: build.mutation<any, DeleteCommentRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('comment_id', data.comment_id.toString());
        formData.append('order_id', data.order_id.toString());

        return {
          url: 'user/deleteComment',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['OrderComments', 'Order'],
    }),

    cancelOrder: build.mutation<CancelOrderResponse, CancelOrderRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('order_id', data.order_id.toString());
        if (data.order_type) formData.append('order_type', data.order_type);
        if (data.cancel_reason) formData.append('cancel_reason', data.cancel_reason);

        return {
          url: 'user/cancelOrder',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Order'],
    }),

    generateInvoice: build.mutation<GenerateInvoiceResponse, GenerateInvoiceRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('order_id', data.order_id.toString());
        if (data.user_id) formData.append('user_id', data.user_id.toString());

        return {
          url: 'user/generateInvoice',
          method: 'POST',
          body: formData,
        };
      },
    }),

    generateCombined: build.mutation<GenerateCombinedResponse, GenerateCombinedRequest>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          // Generate Invoice
          const invoiceFormData = new URLSearchParams();
          invoiceFormData.append('order_id', arg.order_id.toString());
          if (arg.user_id) invoiceFormData.append('user_id', arg.user_id.toString());

          const invoiceResult = await fetchWithBQ({
            url: 'user/generateInvoice',
            method: 'POST',
            body: invoiceFormData,
          });

          let invoiceData: any = null;
          if (!invoiceResult.error) {
            invoiceData = invoiceResult.data;
          }
          
         
          // Generate QR
          const orderType = arg.order_type || 'regular';
          const qrUrl = `https://fftestdeliveryservice.sarsetech.com/qrgenerater/${orderType}/${arg.order_id}/qr`;
          const qrFallbackUrl = `https://fftestdeliveryservice.sarsetech.com/qrgenerate/${orderType}/${arg.order_id}/qr`;
           
          let qrResult = await fetchWithBQ({ url: qrUrl, method: 'GET' });

          if ((qrResult.error && (qrResult.error as any).status === 404) || !qrResult.data) {
            qrResult = await fetchWithBQ({ url: qrFallbackUrl, method: 'GET' });
          }

          let qrData: any = null;
          if (!qrResult.error) {
            qrData = qrResult.data;
          }

          // Try to get QR image
          let qrImageUrl = null;
          if (qrData) {
            try {
              const imageUrl = `https://fftestdeliveryservice.sarsetech.com/qrgenerater/${orderType}/${arg.order_id}/qr/image`;
              const imageFallbackUrl = `https://fftestdeliveryservice.sarsetech.com/qrgenerate/${orderType}/${arg.order_id}/qr/image`;

              // We'll just set the URL, actual download will happen when needed
              qrImageUrl = imageUrl;
            } catch (imgErr) {
              console.warn('QR image URL generation failed:', imgErr);
            }
          }

          return {
            data: {
              error: false,
              message: 'Invoice and QR generated successfully',
              invoice: invoiceData ? {
                invoice_no: invoiceData.invoice_no || '',
                download_url: invoiceData.download_url || '',
              } : undefined,
              qr: qrData ? {
                success: qrData.success || false,
                pickup_code: qrData.pickup_code || qrData.pickupCode || '',
                qr_code: qrData.qr_code || qrData.qrCode || qrData.qr || '',
                qr_image_url: qrImageUrl,
              } : undefined,
            },
          };
        } catch (error: any) {
          console.error('Combined generation failed:', error);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'Failed to generate invoice and QR',
              data: { message: error?.message || 'Unknown error' },
            },
          };
        }
      },
    }),

    generateQR: build.mutation<GenerateQRResponse, GenerateQRRequest>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const orderType =
          arg.order_type === 'guest' || arg.order_type === 'regular' ? arg.order_type : 'regular';
        const primaryUrl = `https://fftestdeliveryservice.sarsetech.com/qrgenerater/${orderType}/${arg.order_id}/qr`;
        const fallbackUrl = `https://fftestdeliveryservice.sarsetech.com/qrgenerate/${orderType}/${arg.order_id}/qr`;

        console.log('generateQR -> trying primary URL', primaryUrl);

        let res = await fetchWithBQ({ url: primaryUrl, method: 'GET' });

        if ((res.error && (res.error as any).status === 404) || !res.data) {
          console.warn('generateQR primary failed, trying fallback', { primary: res, fallbackUrl });
          res = await fetchWithBQ({ url: fallbackUrl, method: 'GET' });
        }

        if (res.error) {
          console.error('generateQR both endpoints failed', res.error);
          return { error: res.error as any };
        }

        try {
          const data = res.data as any;
          const transformed: GenerateQRResponse = {
            success: data.success || false,
            message: data.message || 'QR CODE generated successfully',
            pickup_code: data.pickup_code || data.pickupCode || '',
            qr_code: data.qr_code || data.qrCode || data.qr || '',
            order_id: data.order_id,
            order_type: data.order_type || orderType,
          };
          console.log('generateQR success', transformed);
          return { data: transformed };
        } catch (err) {
          console.error('generateQR transform failed', err);
          return { error: { status: 'CUSTOM_TRANSFORM_ERROR', error: String(err) } as any };
        }
      },
      invalidatesTags: ['Order'],
    }),

    getQrImage: build.query<Blob, { order_id: number; order_type: 'regular' | 'guest' }>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const orderType = arg.order_type || 'regular';
        const primaryUrl = `https://fftestdeliveryservice.sarsetech.com/qrgenerater/${orderType}/${arg.order_id}/qr/image`;
        const fallbackUrl = `https://fftestdeliveryservice.sarsetech.com/qrgenerate/${orderType}/${arg.order_id}/qr/image`;

        console.log('getQrImage -> trying primary URL', primaryUrl);

        let res = await fetchWithBQ({
          url: primaryUrl,
          method: 'GET',
          headers: { Accept: 'image/png' },
          // @ts-ignore
          responseHandler: (response: Response) => response.blob(),
          cache: 'no-cache' as RequestCache,
        } as any);

        if ((res.error && (res.error as any).status === 404) || !res.data) {
          console.warn('getQrImage primary failed, trying fallback', { primary: res, fallbackUrl });
          res = await fetchWithBQ({
            url: fallbackUrl,
            method: 'GET',
            headers: { Accept: 'image/png' },
            // @ts-ignore
            responseHandler: (response: Response) => response.blob(),
            cache: 'no-cache' as RequestCache,
          } as any);
        }

        if (res.error) {
          console.error('getQrImage both endpoints failed', res.error);
          return { error: res.error as any };
        }

        try {
          const blob = res.data as Blob;
          console.log('getQrImage success, blob size:', blob.size);
          return { data: blob };
        } catch (err) {
          console.error('getQrImage transform failed', err);
          return { error: { status: 'CUSTOM_TRANSFORM_ERROR', error: String(err) } as any };
        }
      },
    }),
  }),
});

// ---------- Return API (NEW) ----------
export const returnApi = createApi({
  reducerPath: 'returnApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('vendor_token');
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      headers.set('Content-Type', 'application/x-www-form-urlencoded');
      return headers;
    },
  }),
  tagTypes: ['Return'],
  endpoints: (build) => ({
    getReturnItems: build.query<ReturnItemsResponse, void>({
      query: () => ({
        url: 'user/return-itemslist',
        method: 'POST',
        body: new URLSearchParams(),
      }),
      providesTags: ['Return'],
    }),
    approveReturn: build.mutation<ReturnApproveResponse, ReturnApproveRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('return_id', data.return_id.toString());
        if (data.type !== undefined) formData.append('type', data.type.toString());
        if (data.action !== undefined) formData.append('action', data.action);
        if (data.resolution !== undefined) formData.append('resolution', data.resolution);
        return {
          url: 'user/adminreturnapprove',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Return'],
    }),
  }),
});

// ---------- Exports ----------
export const {
  useListOrdersQuery,
  useUpdateOrderStatusMutation,
  useDeleteOrderMutation,
  useGetOrderCommentsQuery,
  useDeleteCommentMutation,
  useGenerateInvoiceMutation,
  useGenerateCombinedMutation,
  useGenerateQRMutation,
  useGetQrImageQuery,
  useCancelOrderMutation,
  useLazyGetQrImageQuery,
} = orderApi;

export const {
  useGetReturnItemsQuery,
  useApproveReturnMutation,
} = returnApi;